import { isBefore } from 'date-fns';
import getConfig from 'next/config';
import Router from 'next/router';
import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Card, Divider, Icon, Space, Typography } from '@sravni/react-design-system';
import { Edit, Eye, MarkPin, Message } from '@sravni/react-icons';
import { useIsMobile } from '@sravni/react-utils';
import { PAGE_TYPE } from '@sravni/ugc/lib/organizations/constants';
import { getOrganizationHelpers } from '@sravni/ugc/lib/organizations/helpers';
import { checkIfMfo } from '@sravni/ugc/lib/utils/checkOrganizationsType';

import { ENTITY_TYPE } from '../../constants/reviews';
import { EDIT, EDIT_MFO } from '../../constants/routes';
import { getAnalyticsObject, sendReviewEvent, sendSolveReviewEvent } from '../../helpers/analyticsEvents';
import { getReviewUrl } from '../../helpers/reviews';
import type { ICommentItem, IReviewItem } from '../../interfaces/reviews';
import { useUser, useUserInfo } from '../../store/hooks/users';
import { doResolveReview } from '../../store/reducers/reviews';
import { createLinkProps } from '../../utils/routing';
import { AuthorInfo } from '../AuthorInfo';
import { EVENT_ACTIONS } from '../CreateReview/helpers/analytics';
import { Likes } from '../Likes';
import { ProblemSolved } from '../ProblemSolved';
import { IListContentProps } from '../ReviewsList/components/Content';
import { Share } from '../Share';
import { SideBlock } from '../SideBlock';
import { DeleteReview } from '../UserActions/DeleteReview';

import { BadgeBlock } from './components/BadgeBlock';
import { CompanyAnswer } from './components/CompanyAnswer';
import { ExtraInfo } from './components/ExtraInfo';
import { RateStatus } from './components/RateStatus';
import { ReviewBody } from './components/ReviewBody';
import { ReviewInfo } from './components/ReviewInfo';
import { getStatusBadgeInfo } from './helpers/common';
import styles from './review-card.module.scss';

export interface TReviewCardProps extends Pick<IListContentProps, 'tags' | 'type' | 'reviewTypes'> {
  review: IReviewItem;
  companyAnswer?: ICommentItem;
  handleDelete?: () => void;
  showAuthPopup: () => void;
  commentsRef?: React.RefObject<HTMLDivElement>;
  companyAnswerRef?: React.RefObject<HTMLDivElement>;
  sideBlockDescription?: string;
  views?: number;
  isMobile: boolean;
}

export const ReviewCard: React.FC<TReviewCardProps> = ({
  reviewTypes,
  review,
  tags,
  type,
  handleDelete,
  companyAnswer,
  showAuthPopup,
  commentsRef,
  companyAnswerRef,
  sideBlockDescription,
  views,
}) => {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const user = useUser();
  const userInfo = useUserInfo();

  const commentCount = review.commentsCount;
  const isOld = isBefore(new Date(review.date), new Date(2013, 1, 1));
  const resolved = review.problemSolved && !isOld;
  const showProblemSolveBlock =
    !review.isExternal && review.ratingBase < 5 && review.canResolve && !review.problemSolved;

  const isMfo = checkIfMfo(type);
  const statusBadgeInfo = getStatusBadgeInfo({ isResolved: resolved, ratingStatus: review.ratingStatus });
  const gaObj = useMemo(() => getAnalyticsObject(review.organizationName, type as PAGE_TYPE, null, review.id), [
    review.organizationName,
    type,
    review.id,
  ]);

  const isAuthor = Boolean(review.userId) && userInfo?.userId?.toString() === review.userId?.toString();
  const isRepresentative = userInfo?.companyId?.toString() === review.reviewObjectId?.toString();
  const isAdmin =
    user?.role?.includes('Sravni.Reviews.Service-Admin') || user?.role?.includes('Sravni.Reviews.Service-Moderator');
  const showExtraInfo = isAuthor || isRepresentative || isAdmin;

  const orgHelper = getOrganizationHelpers(review.reviewObjectType);

  const editUrl = createLinkProps('/createReview', isMfo ? EDIT_MFO : EDIT, {
    type: orgHelper.itemTypeUrl,
    organization: review.alias,
    id: review.id,
  });

  const reviewUrl = getReviewUrl({
    pageType: type,
    typeAlias: reviewTypes.itemTypeUrl,
    organizationAlias: review.alias,
    reviewId: review.id,
  });

  const handleEditClick = useCallback(
    (type: 'full' | 'extraInfo' = 'full') => () => {
      const orgHelper = getOrganizationHelpers(review.reviewObjectType);

      const label = [orgHelper?.namesType?.bigFirstCharMulti, review.organizationName];

      sendReviewEvent({ label, action: EVENT_ACTIONS.CHANGE_REVIEW });

      let link = editUrl;

      if (type === 'extraInfo') {
        link = createLinkProps('/createReview', isMfo ? EDIT_MFO : EDIT, {
          type: orgHelper.itemTypeUrl,
          organization: review.alias,
          id: review.id,
          queryParams: {
            scrollToExtraInfo: true,
          },
        });
      }

      return Router.push(link.href, link.as);
    },
    [editUrl, isMfo, review.alias, review.id, review.organizationName, review.reviewObjectType],
  );

  const handleSolved = async () => {
    await dispatch(doResolveReview({ id: review.id, problemSolved: true }));
    sendSolveReviewEvent(gaObj);
  };

  const handleScrollToComments = () => {
    const topCoordinate = commentsRef?.current?.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ behavior: 'smooth', top: topCoordinate });
  };

  const shareUrl = () => {
    const { webPath } = getConfig().publicRuntimeConfig;
    const reviewLink = reviewUrl?.as;

    return `${webPath}${reviewLink}`;
  };

  const editButton = () => {
    if (review.canEditReview) {
      return (
        <a href={editUrl.as} title={'Редактировать отзыв'}>
          <Button size={36} variant="outlined" onClick={handleEditClick('full')}>
            <Icon size={20} icon={<Edit />} />
          </Button>
        </a>
      );
    } else if (review.canDelete) {
      return <DeleteReview onDelete={handleDelete} type={ENTITY_TYPE.REVIEW} onlyIcon />;
    }
  };

  const viewsBlock = views > 0 && (
    <Space className={styles.viewsIcon} align={'center'} size={2}>
      <Icon icon={<Eye />} className={'h-color-D30'} />
      <Typography.Text className={'h-color-D30'}>{views}</Typography.Text>
    </Space>
  );

  return (
    <div data-id={review.id}>
      <div className={styles.wrapper}>
        <Space justify={'space-between'} align={'start'} className={'h-mb-20'}>
          <AuthorInfo entity={review} isList={false} showAvatar={!isMobile} />
          <RateStatus review={review} />
        </Space>

        <ReviewBody review={review} isMobile={isMobile} isList={false} />

        <ReviewInfo
          reviewTypes={reviewTypes}
          type={type}
          review={review}
          isMobile={isMobile}
          tags={tags}
          isList={false}
        />

        <Space
          justify={'space-between'}
          align={'center'}
          direction={isMobile ? 'vertical' : 'horizontal'}
          className={'h-mt-16'}
        >
          <Space size={8}>
            {statusBadgeInfo && <BadgeBlock icon={statusBadgeInfo.icon} text={statusBadgeInfo.text} />}
            <BadgeBlock icon={<MarkPin />} text={review.locationData?.name} />
            {!isMobile && viewsBlock}
          </Space>

          <Space justify={isMobile ? 'start' : 'end'} align={'center'} size={8} className={isMobile && 'h-mt-20'}>
            {review.isExternal ? (
              <a href={review.externalUrl} target={'_blank'} rel={'nofollow noopener noreferrer'}>
                Читать на {review.externalSource}
              </a>
            ) : (
              <>
                {isMobile && viewsBlock}
                <Space justify={isMobile ? 'space-between' : 'end'} align={'center'}>
                  <Space size={8} justify={'end'} align={'center'}>
                    {editButton()}

                    <Share shareUrl={shareUrl()} title={review.title} analyticLabel={gaObj} />

                    <Likes reviewId={review.id} votes={review.votes} isUserVoted={review.vote} analyticLabel={gaObj} />

                    <Button size={36} variant="outlined" onClick={handleScrollToComments}>
                      {!!commentCount && commentCount} <Icon size={20} icon={<Message />} />
                    </Button>
                  </Space>
                </Space>
              </>
            )}
          </Space>
        </Space>

        {showExtraInfo && (
          <ExtraInfo
            review={review}
            namesType={reviewTypes.namesType}
            isMobile={isMobile}
            type={type}
            scrollToComments={handleScrollToComments}
            isAuthor={isAuthor}
            handleEditClick={handleEditClick}
          />
        )}

        {companyAnswer && (
          <div ref={companyAnswerRef}>
            <Divider className={styles.wideDivider} />
            <CompanyAnswer
              reviewId={review.id}
              logo={review.logo?.square || review.logo?.round}
              scrollToComments={handleScrollToComments}
              reviewTypes={reviewTypes}
              organizationId={review.reviewObjectId}
              showAuthPopup={showAuthPopup}
            />
          </div>
        )}

        <ProblemSolved
          onSolved={handleSolved}
          reviewTypes={reviewTypes}
          isMobile={isMobile}
          showProblemSolveBlock={showProblemSolveBlock}
        />
      </div>
      {isMobile && (
        <Card className={'h-mb-16'}>
          <SideBlock
            namesType={reviewTypes.namesType}
            title={'Оставьте отзыв'}
            description={sideBlockDescription}
            hasButton
          />
        </Card>
      )}
    </div>
  );
};
